import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { RoiDto, RoiGeometry } from "@gazelab/shared";
import { drawImageWithOverlay, overlayToCanvas, type OverlaySpec } from "../../lib/heatmapCanvas.js";
import { IDENTITY_VIEW, panBy, panelToContent, zoomAt, type ViewState } from "../../lib/zoom.js";
import { rectFromDrag } from "../../lib/roiDraw.js";

export interface HeatmapPanelProps {
  title: string;
  image: HTMLImageElement | null;
  naturalWidth: number;
  naturalHeight: number;
  overlay: OverlaySpec | null;
  view: ViewState;
  onViewChange: (v: ViewState) => void;
  rois: RoiDto[];
  highlightRoiId?: string | null;
  drawMode: boolean;
  onRoiDrawn?: (g: RoiGeometry) => void;
  /** Alto máximo en px CSS del panel. */
  maxHeight?: number;
  emptyMessage?: string;
  testId?: string;
}

/**
 * Panel con la fotografía y la capa de densidad. El panel conserva el aspecto de la imagen, por lo
 * que las coordenadas normalizadas del contenido coinciden con las de la imagen.
 */
export function HeatmapPanel(props: HeatmapPanelProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const drag = useRef<{ kind: "pan" | "roi"; x: number; y: number; start: { x: number; y: number } } | null>(null);
  const [draft, setDraft] = useState<RoiGeometry | null>(null);

  const aspect = props.naturalHeight / props.naturalWidth;
  const maxH = props.maxHeight ?? 560;
  const w = Math.max(0, Math.min(width, maxH / aspect));
  const h = w * aspect;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const overlayCanvas = useMemo(() => (props.overlay ? overlayToCanvas(props.overlay) : null), [props.overlay]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || w <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    const ctx = c.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#e2e8f0";
    ctx.fillRect(0, 0, w, h);
    ctx.save();
    ctx.translate(props.view.tx, props.view.ty);
    ctx.scale(props.view.scale, props.view.scale);
    drawImageWithOverlay(ctx, props.image, overlayCanvas, 0, 0, w, h);
    const drawRect = (g: RoiGeometry, color: string, label?: string, dashed = false) => {
      ctx.save();
      ctx.lineWidth = 2 / props.view.scale;
      ctx.strokeStyle = color;
      if (dashed) ctx.setLineDash([6 / props.view.scale, 4 / props.view.scale]);
      ctx.strokeRect(g.x * w, g.y * h, g.width * w, g.height * h);
      if (label) {
        const fs = 12 / props.view.scale;
        ctx.font = `600 ${fs}px system-ui, sans-serif`;
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillRect(g.x * w, g.y * h, tw + 6 / props.view.scale, fs + 4 / props.view.scale);
        ctx.fillStyle = "#0f172a";
        ctx.fillText(label, g.x * w + 3 / props.view.scale, g.y * h + fs);
      }
      ctx.restore();
    };
    for (const r of props.rois) drawRect(r.geometry, r.id === props.highlightRoiId ? "#facc15" : "#ffffff", r.label);
    if (draft) drawRect(draft, "#facc15", undefined, true);
    ctx.restore();
  }, [w, h, props.view, props.image, overlayCanvas, props.rois, props.highlightRoiId, draft]);

  // Zoom con rueda (listener no pasivo para poder prevenir el desplazamiento de la página).
  const viewRef = useRef(props.view);
  viewRef.current = props.view;
  const onViewRef = useRef(props.onViewChange);
  onViewRef.current = props.onViewChange;
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = c.getBoundingClientRect();
      onViewRef.current(zoomAt(viewRef.current, e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height));
    };
    c.addEventListener("wheel", onWheel, { passive: false });
    return () => c.removeEventListener("wheel", onWheel);
  }, []);

  function local(e: ReactPointerEvent) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: ReactPointerEvent) {
    if (e.button !== 0) return;
    const p = local(e);
    (e.target as Element).setPointerCapture(e.pointerId);
    const start = panelToContent(props.view, p.x, p.y, w, h);
    drag.current = { kind: props.drawMode ? "roi" : "pan", x: p.x, y: p.y, start };
  }

  function onPointerMove(e: ReactPointerEvent) {
    const d = drag.current;
    if (!d) return;
    const p = local(e);
    if (d.kind === "pan") {
      props.onViewChange(panBy(props.view, p.x - d.x, p.y - d.y, w, h));
      d.x = p.x;
      d.y = p.y;
    } else {
      setDraft(rectFromDrag(d.start, panelToContent(props.view, p.x, p.y, w, h)));
    }
  }

  function onPointerUp(e: ReactPointerEvent) {
    const d = drag.current;
    drag.current = null;
    if (d?.kind === "roi") {
      const g = rectFromDrag(d.start, panelToContent(props.view, local(e).x, local(e).y, w, h));
      setDraft(null);
      if (g) props.onRoiDrawn?.(g);
    }
  }

  return (
    <figure className="min-w-0" data-testid={props.testId}>
      <figcaption className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-800">
        <span>{props.title}</span>
        {props.view.scale > 1 && (
          <button type="button" className="text-xs font-normal text-sky-800 hover:underline" onClick={() => props.onViewChange(IDENTITY_VIEW)}>
            Restablecer zoom ({props.view.scale.toFixed(1)}×)
          </button>
        )}
      </figcaption>
      <div ref={wrapRef} className="relative w-full">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${props.title}: fotografía con mapa de distribución de atención estimada`}
          style={{ width: w, height: h, touchAction: "none", cursor: props.drawMode ? "crosshair" : props.view.scale > 1 ? "grab" : "zoom-in" }}
          className="rounded-md ring-1 ring-slate-300"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            drag.current = null;
            setDraft(null);
          }}
        />
        {!props.overlay && props.emptyMessage && w > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-2 mx-auto w-fit rounded bg-white/90 px-2 py-1 text-xs text-slate-700 shadow" style={{ maxWidth: w }}>
            {props.emptyMessage}
          </div>
        )}
      </div>
    </figure>
  );
}
